/**
 * The Board's "/" menu: what it offers, how a typed query narrows it, and
 * when the text before the caret opens it. Pure, so the menu's behaviour is
 * tested without an editor.
 *
 * @module components/spaces/board/boardCommands
 */

export type BoardCommandId =
  | "heading1"
  | "heading2"
  | "heading3"
  | "bullets"
  | "numbers"
  | "tasks"
  | "table"
  | "chart"
  | "quote"
  | "divider"
  | "code";

export interface BoardCommand {
  readonly id: BoardCommandId;
  readonly label: string;
  readonly hint: string;
  readonly keywords: ReadonlyArray<string>;
}

export const BOARD_COMMANDS: ReadonlyArray<BoardCommand> = [
  { id: "heading1", label: "Heading 1", hint: "Big section title", keywords: ["h1", "title"] },
  { id: "heading2", label: "Heading 2", hint: "Section", keywords: ["h2", "section"] },
  { id: "heading3", label: "Heading 3", hint: "Sub-section", keywords: ["h3"] },
  { id: "tasks", label: "Checklist", hint: "Deliveries and to-dos", keywords: ["todo", "task", "check", "delivery"] },
  { id: "bullets", label: "Bulleted list", hint: "Simple list", keywords: ["ul", "list"] },
  { id: "numbers", label: "Numbered list", hint: "Ordered steps", keywords: ["ol", "ordered"] },
  { id: "table", label: "Table", hint: "3 × 3 grid", keywords: ["grid", "rows"] },
  { id: "chart", label: "Chart", hint: "ECharts figure from JSON", keywords: ["graph", "plot", "figure", "bar"] },
  { id: "quote", label: "Quote", hint: "Call out a passage", keywords: ["blockquote"] },
  { id: "divider", label: "Divider", hint: "Horizontal rule", keywords: ["hr", "line", "rule"] },
  { id: "code", label: "Code", hint: "Monospace block", keywords: ["pre", "snippet"] },
];

/** Commands whose label or keywords start with a word of `query`, best first. */
export function filterBoardCommands(query: string): BoardCommand[] {
  const wanted = query.trim().toLowerCase();
  if (wanted === "") return [...BOARD_COMMANDS];
  const score = (command: BoardCommand): number => {
    const label = command.label.toLowerCase();
    if (label.startsWith(wanted)) return 0;
    if (label.split(/\s+/).some((word) => word.startsWith(wanted))) return 1;
    if (command.keywords.some((keyword) => keyword.startsWith(wanted))) return 2;
    return -1;
  };
  return BOARD_COMMANDS.map((command) => ({ command, rank: score(command) }))
    .filter((entry) => entry.rank >= 0)
    .sort((a, b) => a.rank - b.rank)
    .map((entry) => entry.command);
}

const SLASH_QUERY = /(?:^|\s)\/([a-z0-9-]{0,24})$/i;

/**
 * The query after a "/" that starts a word right before the caret, or null.
 * `textBefore` is the current block's text up to the caret, so a "/" in a
 * URL or a date ("1/2") never opens the menu.
 */
export function slashQuery(textBefore: string): string | null {
  const match = SLASH_QUERY.exec(textBefore);
  return match ? match[1]! : null;
}

/** The chart a "/chart" inserts: a small, valid option to edit from. */
export const STARTER_CHART = JSON.stringify(
  {
    title: { text: "New chart" },
    xAxis: { type: "category", data: ["Q1", "Q2", "Q3", "Q4"] },
    yAxis: { type: "value" },
    series: [{ type: "bar", data: [12, 18, 15, 22] }],
  },
  null,
  2,
);
